// ฟังก์ชันสำหรับคำนวณราคาและเหรียญแยกกัน
// เพื่อให้ระบบสามารถเก็บค่าของผู้ใช้งานระบบสำหรับเจ้าของระบบ

/**
 * คำนวณราคาเหรียญจากราคาค่าบริการจริง
 * @param {number} servicePrice - ราคาค่าบริการจริง (บาท)
 * @param {number} commission - ค่าคอมมิชชั่นของระบบ (0-1)
 * @param {number} coinRate - อัตราแลกเปลี่ยนเหรียญต่อบาท (เช่น 1 เหรียญ = 30 บาท)
 * @returns {number} จำนวนเหรียญที่ลูกค้าต้องจ่าย
 */
export const calculateCoinPrice = (servicePrice, commission = 0.3, coinRate = 30) => {
  // คำนวณราคาหลังหักคอมมิชชั่น
  const priceAfterCommission = servicePrice * (1 - commission);
  
  // แปลงเป็นเหรียญ
  const coinPrice = Math.ceil(priceAfterCommission / coinRate);
  
  return coinPrice;
};

/**
 * คำนวณรายได้ของระบบจากค่าคอมมิชชั่น
 * @param {number} servicePrice - ราคาค่าบริการจริง (บาท)
 * @param {number} commission - ค่าคอมมิชชั่นของระบบ (0-1)
 * @returns {number} รายได้ของระบบ (บาท)
 */
export const calculateSystemRevenue = (servicePrice, commission = 0.3) => {
  return servicePrice * commission;
};

/**
 * คำนวณรายได้ของน้องจากค่าบริการ
 * @param {number} servicePrice - ราคาค่าบริการจริง (บาท)
 * @param {number} commission - ค่าคอมมิชชั่นของระบบ (0-1)
 * @returns {number} รายได้ของน้อง (บาท)
 */
export const calculateGirlRevenue = (servicePrice, commission = 0.3) => {
  return servicePrice * (1 - commission);
};

/**
 * คำนวณราคารวมสำหรับการจอง
 * @param {object} girlData - ข้อมูลน้อง
 * @param {number} duration - ระยะเวลา (ชั่วโมง)
 * @returns {object} ข้อมูลราคารวม
 */
export const calculateTotalPrice = (girlData, duration = 1) => {
  // ตรวจสอบว่า girlData มีอยู่หรือไม่
  if (!girlData) {
    return {
      totalCoinPrice: 0,
      totalServicePrice: 0,
      systemRevenue: 0,
      girlRevenue: 0,
      commission: 0.3,
      duration: 1,
      coinPricePerHour: 0,
      servicePricePerHour: 0
    };
  }
  
  const servicePrice = girlData.servicePrice || girlData.prices?.servicePrice || 0;
  const coinPrice = girlData.coinPrice || girlData.prices?.coinPrice || girlData.prices?.base || 0;
  const commission = girlData.commission || 0.3;
  
  const totalServicePrice = servicePrice * duration;
  const totalCoinPrice = coinPrice * duration;
  const totalSystemRevenue = calculateSystemRevenue(totalServicePrice, commission);
  const totalGirlRevenue = calculateGirlRevenue(totalServicePrice, commission);
  
  return {
    // ราคาสำหรับลูกค้า
    totalCoinPrice, // เหรียญที่ลูกค้าต้องจ่าย
    totalServicePrice, // ราคาค่าบริการจริงรวม
    
    // รายได้แยกตามฝ่าย
    systemRevenue: totalSystemRevenue, // รายได้ของระบบ
    girlRevenue: totalGirlRevenue, // รายได้ของน้อง
    
    // ข้อมูลเพิ่มเติม
    commission,
    duration,
    coinPricePerHour: coinPrice,
    servicePricePerHour: servicePrice
  };
};

/**
 * แปลงเหรียญเป็นเงินบาท
 * @param {number} coins - จำนวนเหรียญ
 * @param {number} coinRate - อัตราแลกเปลี่ยนเหรียญต่อบาท
 * @returns {number} เงินบาท
 */
export const convertCoinsToBaht = (coins, coinRate = 30) => {
  return coins * coinRate;
};

/**
 * แปลงเงินบาทเป็นเหรียญ
 * @param {number} baht - เงินบาท
 * @param {number} coinRate - อัตราแลกเปลี่ยนเหรียญต่อบาท
 * @returns {number} จำนวนเหรียญ
 */
export const convertBahtToCoins = (baht, coinRate = 30) => {
  return Math.ceil(baht / coinRate);
};

/**
 * ฟังก์ชันสำหรับอัปเดตราคาเหรียญตามราคาค่าบริการใหม่
 * @param {object} girlData - ข้อมูลน้อง
 * @param {number} newServicePrice - ราคาค่าบริการใหม่ (บาท)
 * @param {number} commission - ค่าคอมมิชชั่นของระบบ
 * @param {number} coinRate - อัตราแลกเปลี่ยนเหรียญต่อบาท
 * @returns {object} ข้อมูลราคาใหม่
 */
export const updatePricing = (girlData, newServicePrice, commission = 0.3, coinRate = 30) => {
  const newCoinPrice = calculateCoinPrice(newServicePrice, commission, coinRate);
  
  return {
    ...girlData,
    servicePrice: newServicePrice,
    coinPrice: newCoinPrice,
    commission,
    prices: {
      ...girlData.prices,
      base: newCoinPrice, // backward compatibility
      servicePrice: newServicePrice,
      coinPrice: newCoinPrice
    }
  };
};

/**
 * ฟังก์ชันสำหรับตรวจสอบความถูกต้องของราคา
 * @param {object} girlData - ข้อมูลน้อง
 * @returns {object} ผลการตรวจสอบ
 */
export const validatePricing = (girlData) => {
  const errors = [];
  const warnings = [];
  
  // ตรวจสอบข้อมูลที่จำเป็น
  if (!girlData.servicePrice || girlData.servicePrice <= 0) {
    errors.push('ราคาค่าบริการต้องมากกว่า 0');
  }
  
  if (!girlData.coinPrice || girlData.coinPrice <= 0) {
    errors.push('ราคาเหรียญต้องมากกว่า 0');
  }
  
  if (!girlData.commission || girlData.commission < 0 || girlData.commission > 1) {
    errors.push('ค่าคอมมิชชั่นต้องอยู่ระหว่าง 0-1');
  }
  
  // ตรวจสอบความสมเหตุสมผล
  if (girlData.servicePrice && girlData.coinPrice) {
    const expectedCoinPrice = calculateCoinPrice(girlData.servicePrice, girlData.commission);
    const difference = Math.abs(girlData.coinPrice - expectedCoinPrice);
    
    if (difference > 5) { // ถ้าต่างกันมากกว่า 5 เหรียญ
      warnings.push(`ราคาเหรียญอาจไม่สอดคล้องกับราคาค่าบริการ (คาดหวัง: ${expectedCoinPrice} เหรียญ)`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

